import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { StudentWithConversation } from '../types';
import {
  getStudentsForClass,
  analyzeClassConversations,
  getAnalysisJobStatus,
} from '../services/api';
import { AnalysisStatus } from '../types';
import { resolveTeacherClassContext, saveTeacherClassContext } from '../utils/teacherClassContext';
import TeacherLayout from './teacher/TeacherLayout';

const isTimeoutError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return err.code === 'ECONNABORTED' || status === 503 || status === 504 || !err.response;
};

// Section icons mapped by heading keyword
const SECTION_ICONS: Record<string, string> = {
  topic: '📚',
  interest: '📚',
  curiosity: '🔍',
  depth: '🧠',
  thinking: '🧠',
  communication: '💬',
  style: '💬',
  growth: '🌱',
  suggestion: '💡',
  recommend: '💡',
  standout: '⭐',
  attention: '⚠️',
  engagement: '🎯',
  pattern: '🔄',
  popular: '🔥',
  class: '🏫',
  theme: '🏷️',
};

const getSectionIcon = (heading: string): string => {
  const lower = heading.toLowerCase();
  for (const [key, icon] of Object.entries(SECTION_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return '📋';
};

const SECTION_COLOURS = [
  'from-indigo-50 to-indigo-50/30 border-indigo-200',
  'from-purple-50 to-purple-50/30 border-purple-200',
  'from-blue-50 to-blue-50/30 border-blue-200',
  'from-teal-50 to-teal-50/30 border-teal-200',
  'from-emerald-50 to-emerald-50/30 border-emerald-200',
  'from-amber-50 to-amber-50/30 border-amber-200',
];

interface AnalysisSection {
  heading: string;
  body: string;
}

const parseSections = (raw: string): AnalysisSection[] => {
  const lines = raw.split('\n');
  const sections: AnalysisSection[] = [];
  let current: AnalysisSection | null = null;

  for (const line of lines) {
    const headingMatch =
      line.match(/^\*\*(.+?)\*\*\s*$/) ||
      line.match(/^\*\*(\d+\.\s+.+?)\**\s*$/) ||
      line.match(/^#{1,3}\s+(.+)$/);

    if (headingMatch) {
      if (current) sections.push(current);
      current = { heading: headingMatch[1].replace(/[*:]+$/, '').trim(), body: '' };
    } else if (current) {
      current.body += line + '\n';
    } else if (line.trim()) {
      current = { heading: 'Overview', body: line + '\n' };
    }
  }
  if (current) sections.push(current);
  return sections.filter((s) => s.body.trim());
};

const LoadingShimmer: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-2xl border border-slate-100 bg-slate-50 p-5 space-y-3">
        <div className="h-4 w-1/3 rounded-full bg-slate-200" />
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-slate-200" />
          <div className="h-3 w-5/6 rounded bg-slate-200" />
          <div className="h-3 w-4/6 rounded bg-slate-200" />
        </div>
      </div>
    ))}
  </div>
);

interface ClassSummaryState {
  school?: string;
  grade?: number | string;
  section?: string | null;
}

const ClassSummary: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = resolveTeacherClassContext((location.state as ClassSummaryState) || {});
  const { school, grade, section } = state;

  const [students, setStudents] = useState<StudentWithConversation[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('ready');
  const [jobId, setJobId] = useState<string | null>(null);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isAnalysisInProgress = analysisStatus === 'queued' || analysisStatus === 'running';
  const showLoading = (isInitialLoad && !analysis) || (isAnalysisInProgress && !analysis);

  const isMountedRef = useRef(true);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (job: string) => {
      if (!isMountedRef.current) return;
      try {
        const data = await getAnalysisJobStatus(job);
        if (!isMountedRef.current) return;

        const nextStatus: AnalysisStatus =
          data.analysis_status ??
          (data.status === 'failed'
            ? 'failed'
            : data.status === 'completed'
            ? 'ready'
            : 'running');

        if (data.analysis !== undefined) setAnalysis(data.analysis ?? null);
        if (data.computed_at) setComputedAt(data.computed_at);

        if (data.status === 'failed' || nextStatus === 'failed') {
          setAnalysisStatus('failed');
          setAnalysisError(data.error_message ?? 'Analysis failed. Please try again.');
          setJobId(null);
          stopPolling();
          return;
        }
        if (data.status === 'completed' || nextStatus === 'ready') {
          setAnalysisStatus('ready');
          setAnalysisError(null);
          setJobId(null);
          stopPolling();
          return;
        }
        setAnalysisStatus(nextStatus);
        pollTimeoutRef.current = setTimeout(() => pollJob(job), 4000);
      } catch (err) {
        if (!isMountedRef.current) return;
        setAnalysisStatus('failed');
        setAnalysisError(err instanceof Error ? err.message : 'Failed to fetch analysis status.');
        setJobId(null);
        stopPolling();
      }
    },
    [stopPolling]
  );

  const gradeNumber = useMemo(() => {
    if (typeof grade === 'number') return grade;
    if (typeof grade === 'string') {
      const parsed = Number(grade);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }, [grade]);

  useEffect(() => {
    if (!school || !gradeNumber) return;
    saveTeacherClassContext({ school, grade: gradeNumber, section });
  }, [school, gradeNumber, section]);

  useEffect(() => {
    if (!school || !gradeNumber) {
      navigate('/teacher-view', { replace: true });
      return;
    }
    let isMounted = true;
    const fetchStudents = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getStudentsForClass(school, gradeNumber, section);
        if (isMounted) setStudents(data);
      } catch (err) {
        if (isMounted) setError('Failed to fetch students. Please try again.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [school, gradeNumber, section, navigate]);

  const fetchAnalysis = useCallback(
    async (forceRefresh = false) => {
      if (!school || !gradeNumber) return;
      if (!isMountedRef.current) return;

      setAnalysisError(null);
      if (forceRefresh) setIsRefreshing(true);

      try {
        const data = await analyzeClassConversations(school, gradeNumber, section, forceRefresh);
        if (!isMountedRef.current) return;

        if (data.analysis !== undefined) setAnalysis(data.analysis ?? null);
        setComputedAt(data.computed_at ?? null);

        const shouldPoll = Boolean(
          data.job_id && (data.status === 'queued' || data.status === 'running')
        );
        const nextStatus: AnalysisStatus =
          data.status === 'failed' ? 'failed' : shouldPoll ? data.status : 'ready';

        setAnalysisStatus(nextStatus);
        setIsRefreshing(false);
        setIsInitialLoad(false);

        if (shouldPoll) {
          setJobId(data.job_id as string);
          stopPolling();
          pollJob(data.job_id as string);
        } else {
          setJobId(null);
          stopPolling();
        }
        if (nextStatus === 'failed') setAnalysisError('Analysis failed. Please try again.');
      } catch (err) {
        if (!isMountedRef.current) return;
        setIsRefreshing(false);
        setIsInitialLoad(false);
        if (isTimeoutError(err)) {
          setAnalysisStatus('running');
          setAnalysisError('Analysis is being prepared. Please refresh in a moment.');
          return;
        }
        setAnalysisStatus('failed');
        setJobId(null);
        setAnalysisError(
          err instanceof Error ? err.message : 'Failed to generate analysis. Please try again.'
        );
        stopPolling();
      }
    },
    [school, gradeNumber, section, pollJob, stopPolling]
  );

  useEffect(() => {
    if (!school || !gradeNumber) return;
    fetchAnalysis();
  }, [school, gradeNumber, section, fetchAnalysis]);

  const sections = analysis ? parseSections(analysis) : [];

  const studentCount = students?.length ?? 0;
  const activeStudents = students?.filter((s) => s.latest_conversation != null).length ?? 0;

  return (
    <TeacherLayout active="home">
      <div className="mx-auto w-full max-w-3xl space-y-6">

        {/* Header card */}
        <div className="rounded-3xl bg-white shadow-lg shadow-slate-200/60 px-8 py-8">
          <div className="mb-6">
            <button
              type="button"
              onClick={() => navigate('/teacher-dashboard', { state: { school, grade: gradeNumber ?? grade, section } })}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-800"
            >
              ← Back
            </button>
          </div>

          {/* Title row */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-xl shadow-md">
                  🏫
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Class Summary</h1>
                  {computedAt && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Last updated {new Date(computedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => fetchAnalysis(true)}
              disabled={isRefreshing || (isAnalysisInProgress && !analysis)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow transition hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRefreshing ? (
                <>
                  <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Refreshing…
                </>
              ) : (
                <>↻ Refresh</>
              )}
            </button>
          </div>

          {/* Class meta chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            {school && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                🏫 {school}
              </span>
            )}
            {gradeNumber && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600">
                📘 Grade {gradeNumber}
              </span>
            )}
            {section && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600">
                ✏️ Section {section}
              </span>
            )}
          </div>

          {/* Stats row */}
          {!isLoading && students && (
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-[#E9E6F4] bg-white px-4 py-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{studentCount}</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">Students</p>
              </div>
              <div className="rounded-2xl border border-[#E9E6F4] bg-violet-50/60 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-violet-700">{activeStudents}</p>
                <p className="mt-0.5 text-xs font-medium text-violet-500">Active</p>
              </div>
              <div className="rounded-2xl border border-[#E9E6F4] bg-emerald-50/60 px-4 py-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">
                  {activeStudents > 0 ? Math.round((activeStudents / studentCount) * 100) : 0}%
                </p>
                <p className="mt-0.5 text-xs font-medium text-emerald-500">Participation</p>
              </div>
            </div>
          )}

          {/* Error banner */}
          {analysisError && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <p className="text-sm text-red-600">{analysisError}</p>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="rounded-3xl bg-white shadow-lg shadow-slate-200/60 px-8 py-8">
            <LoadingShimmer />
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-red-50 border border-red-100 px-6 py-5">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : studentCount === 0 ? (
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-6 py-5">
            <p className="text-sm text-amber-700">No students found for this class.</p>
          </div>
        ) : showLoading ? (
          <div className="rounded-3xl bg-white shadow-lg shadow-slate-200/60 px-8 py-8">
            <p className="text-sm font-medium text-indigo-600 mb-4 animate-pulse">
              Generating class analysis…
            </p>
            <LoadingShimmer />
          </div>
        ) : sections.length > 0 ? (
          <div className="space-y-4">
            {sections.map((section, idx) => (
              <div
                key={idx}
                className={`rounded-2xl border bg-gradient-to-br ${SECTION_COLOURS[idx % SECTION_COLOURS.length]} px-6 py-5 shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl" aria-hidden>
                    {getSectionIcon(section.heading)}
                  </span>
                  <h2 className="text-base font-semibold text-slate-800">{section.heading}</h2>
                </div>
                <div className="prose prose-sm prose-slate max-w-none text-slate-700 leading-relaxed">
                  <ReactMarkdown>{section.body.trim()}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        ) : !analysisError && analysisStatus === 'ready' ? (
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-6 py-5">
            <p className="text-sm text-amber-700">
              No analysis available yet. Click <strong>Refresh</strong> to generate one.
            </p>
          </div>
        ) : null}
      </div>
    </TeacherLayout>
  );
};

export default ClassSummary;
